import { SequenceStatus } from '../../../shared/models/sequenceStatus.enum';
import { ResponseLevel } from '../../MaintenanceTriggers/MaintenanceTrigger/MaintenanceTriggerForm.slice';
import { ImagesResponse, Sequence, SequenceImage } from '../../StitchingTool/Stitching/Stitching.reducer';
import { DataQCAssessment } from './Tree/QC/qc.slice';

export type SequenceResponse = {
  stitchedViewData: ImagesResponse | null;
  sequence: SequenceInfo;
};

export type ImageRawData = {
  name: string;
  src: string;
  scale: number;
  width: number;
  height: number;
  rotation: number;
  left: number;
  top: number;
  overlaps: Array<ImageOverlap>;
  dataQCAssessment: DataQCAssessment | null;
};

export type ImageOverlap = {
  imageFileName: string;
  shape: Array<ShapePoint>;
  isVisible: boolean;
};

export type SequenceInfo = {
  id: string;
  inspection: string;
  inspectionDate: string;
  inspectionType: string;
  site: string;
  turbineName: string;
  bladePosition: string;
  bladeSide: string;
  status: SequenceStatus;
  images: Array<ImageInfo>;
  hasStitchedView: boolean;
  maintenanceTriggerId: string;
  subsampleFactor: number;
};

export type ImageInfo = {
  id: string;
  width: number;
  height: number;
  filename: string;
  filenameToShow?: string;
  isHidden: boolean;
  top?: number;
  annotationsCount: number;
  annotationsToBeTransferredCount: number;
  isPhotocard: boolean;
  isDetailed: boolean;
  isOverview: boolean;
  parentFilename: string;
  groupNumber?: number;
  distanceToRoot?: number;
  stackingRank: number;
  haveQC: boolean;
  annotations: Array<AnnotationsForGrid>;
  overlaps: Array<ImageOverlap>;
};

export type ImagesAndSequence = {
  images: SequenceImage[];
  subsampleFactor: number;
  newCache: Sequence;
};

export enum NewFindingStatus {
  WaitingForReview,
  ReviewCompleted,
  UserReviewCompleted,
  QaReviewPassed,
  QaReviewFailed,
  NotApplicable,
  WaitingForAssessment,
  AssessmentInProgress,
  UnderInvestigation,
  AssessmentCompleted,
  Published,
  QAIssueFixed
}

export const ASSESSMENT_STATUSES: NewFindingStatus[] = [
  NewFindingStatus.WaitingForAssessment,
  NewFindingStatus.AssessmentInProgress,
  NewFindingStatus.UnderInvestigation,
  NewFindingStatus.AssessmentCompleted
];

export enum FindingTypeSuggestion {
  StructuralCrack = 1,
  SuperficialCrack = 2,
  Erosion = 3,
  Delamination = 4,
  Contamination = 5,
  CosmeticDamage = 6,
  LPS = 7,
  AddOn = 8
}

export type AnnotationsForGrid = {
  id: string;
  shape: ShapePoint[];
  user: string;
  userId: string;
  dateCreated: string;
  annotationSource: number;
  angle: number;
  imageScale: number;
  isMlProposal: boolean;
  isReviewed: boolean;
  isQaReviewed: boolean;
  description: string;
  annotationStandardId: string;
  annotationStandardName: string;
  findingTypeId: string;
  findingTypeName: string;
  properties: AnnotationProperty[];
  externalId: string;
  homographyShape: ShapePoint[];
  imageId: string;
  sequencePath: string;
  imageName: string;
  statusText: string;
  newStatus: NewFindingStatus | null;
  dirty: boolean;
  responseLevel: ResponseLevel;
  visible?: boolean;
  repairEstimateHours: string;
  failureMechanismTemplateId?: string;
  findingTypeSuggestion?: FindingTypeSuggestion;
  findingTypeMlSuggestion?: string;
  canBeDeleted: boolean;
  showQAReviewPassedOption: boolean;
};

export type AnnotationProperty = {
  findingTypePropertyId?: string;
  name: string;
  value: string;
};

export type ShapePoint = {
  x: number;
  y: number;
};

export type PolygonCoordinate = {
  x: Array<number>;
  y: Array<number>;
};

export type MLRequest = {
  sequenceId?: string;
  imageFileName?: string;
  polygonCoordinates?: PolygonCoordinate;
};

export type TurbineSequencesStatus = {
  message: string;
  canAnnotate: boolean;
  exist: boolean;
  sequences: Array<SequenceStatusInfo>;
  annotationStandardId: string;
  canPublish: boolean;
};

export enum MLAssessmentScore {
  NotUsable = 1,
  PartiallyUsable = 2,
  MajorAdjustmentNeeded = 3,
  MinorAdjustmentNeeded = 4,
  CanProcesAutomatically = 5
}

export type SequenceStatusInfo = {
  blade: string;
  bladeSide: string;
  sequencePath: string;
  status: SequenceStatus;
  annotationsCount: number;
  annotationsToBeTransferredCount: number;
  haveQC: boolean;
  mlAssessmentScore?: MLAssessmentScore;
  useMlFindingDetection?: boolean;
};

export type Localization = {
  succeeded: boolean;
  info: string;
  version: string;
  sequenceId: string;
  imageFileName: string;
  distanceToRoot: number;
  distanceToLeadingEdge?: number;
  area: number;
  boundingBoxDiagonal: number;
  width: number;
  height: number;
  coordinatesOnImage: { x: number[]; y: number[] };
  coordinatesOnCanvas: { x: number[]; y: number[] };
};

export type AnnotationSettings = {
  canAnnotate: boolean;
  callMLServices: boolean;
  localizationEditable: boolean;
};

export type SelectOption = {
  value: string;
  text: string;
};

export type SaveImageSettings = {
  fileNames: string[];
  imageSettings: ImageSettings;
};

export type ImageSettings = {
  contrast: number;
  brightness: number;
  saturation: number;
  applyToAll: boolean;
};

export type ImagesAndSequenceInfo = ImagesAndSequence & { info: SequenceInfo };

export type IndexRange = {
  from: number;
  to: number;
};

export type CachedImage = {
  url: string;
  image: ImageRawData;
};

export type CachedSequence = {
  sequence: Sequence;
  info: SequenceInfo;
};

export type PathContextType = 'no-images' | 'with-images';
